'use client'

import { useEffect, useRef } from 'react'

import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { faChartScatter } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { GridApi } from 'ag-grid-community'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useIsTranslationDefined } from '@b-stock/fe-core/bstock-next/hooks'
import {
  Button,
  ButtonSizes,
  EmptyState,
  FormattedMessage,
  Modal,
  ModalSnackbarContainer,
  showSnackbar,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { Fullscreen } from '@/components/common/modal/Fullscreen'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { DealGraphLegend } from './DealGraphLegend'
import { useDealGraphData } from './useDealGraphData'

const SNACKBAR_CONTAINER_ID = 'deal-graph'

/*
 * Fullscreen on a phone; on anything wider, inset from the viewport so the List
 * View it plots stays visible around it.
 */
const InsetModal = styled(Modal)`
  @media ${Breakpoints.min.medium} {
    border-radius: 0.5rem;
    bottom: 2.5rem;
    box-shadow: 0 1rem 3rem rgb(20 26 30 / 28%);
    left: 5rem;
    right: 5rem;
    top: 2.5rem;
  }
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const Subtitle = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
`

const Description = styled.span`
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const CompactDescription = styled.span`
  @media ${Breakpoints.min.medium} {
    display: none;
  }
`

const FooterStart = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;

  @media ${Breakpoints.max.small} {
    flex-direction: row-reverse;
    gap: 1rem;
    justify-content: space-between;
    width: 100%;
  }
`

const Excluded = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
`

// Matches the X on fe-core's `Modal.Header`, at a 44px hit target.
const HeaderCloseButton = styled.button`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  color: ${Colors.Semantic.Neutral.black};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-size: 1.5rem;
  height: 2.75rem;
  justify-content: center;
  padding: 0;
  width: 2.75rem;
`

// The header's own close action stands in for this one on a phone.
const CloseButton = styled(Button)`
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

// On a phone the table behind is out of sight; on desktop, Close says it.
const ListButton = styled(Button)`
  @media ${Breakpoints.min.medium} {
    display: none;
  }
`

// wraps Spinner or EmptyState
const Centered = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;
`

/*
 * `DealGraph` is the only runtime importer of AG Charts in the app, so
 * deferring it here keeps the chart engine out of the ALP's initial JavaScript
 * — it was fetched on every page load, even with the feature flag off, because
 * the flag gates rendering and not importing.
 *
 * The boundary sits here rather than around the whole modal so the chrome stays
 * static: the header, the counts, and the Close action are all live while the
 * engine downloads, and there is only one definition of them to maintain.
 *
 * The fallback matches what `AgChart` shows for `isFetching`, so a slow
 * connection and a slow query look the same to the buyer.
 */
const DealGraph = dynamic(
  () => import('./DealGraph').then((module) => module.DealGraph),
  {
    loading: () => (
      <Centered>
        <Spinner text="" />
      </Centered>
    ),
    ssr: false,
  }
)

/**
 * The Deal Graph, as a fullscreen modal over the List View.
 *
 * A modal rather than a swapped-in view: the graph plots the rows the table is
 * currently showing, so the page's pagination, sort, and filter controls all
 * still describe it. Replacing the table left those controls on screen
 * governing something the buyer could no longer see.
 */
export const DealGraphModal = ({
  error,
  gridApi,
  isFetching,
  onClose,
}: {
  error?: Error | null
  gridApi: GridApi<CombinedListingSearchResult> | null
  isFetching: boolean
  onClose: () => void
}) => {
  const { formatMessage } = useIntl()
  const { track } = useAnalytics()
  const { data, excludedCount } = useDealGraphData(gridApi)
  const { data: search, state } = useListingSearch()

  // A valid sortBy/sortOrder pair can still lack a label (e.g. endTime desc
  // from the URL); the subtitle then leaves the sort out.
  const sortId = useIsTranslationDefined(
    `Filters.listingSort.${state.sortBy}.${state.sortOrder}`
  )
  const position = {
    page: state.page,
    pageCount: search ? Math.max(1, Math.ceil(search.total / search.limit)) : 1,
    sort: sortId ? formatMessage({ id: sortId }) : 'UNNAMED',
  }

  // Once per open, on the first grid read: the hook reads in an effect, so the
  // first render has no rows, and the graph only opens when the table has some.
  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (hasTrackedView.current || data.length + excludedCount === 0) return
    hasTrackedView.current = true
    track('alp_deal_graph_viewed', {
      excluded_no_fmv_count: excludedCount,
      plotted_count: data.length,
      visible_row_count: data.length + excludedCount,
    })
  }, [data.length, excludedCount, track])

  // A failed refetch leaves the grid's rows, and so the plot, as they were.
  // The page's own snackbars sit under the modal, hence the container inside it.
  useEffect(() => {
    if (!error) return
    showSnackbar({
      content: formatMessage({
        id: 'Listings.ListView.dealGraph.refreshError',
      }),
      options: {
        autoClose: true,
        containerId: SNACKBAR_CONTAINER_ID,
        group: 'refreshError',
        type: 'error',
      },
    })
  }, [error, formatMessage])

  return (
    <InsetModal appearance="fullscreen" closeModal={onClose}>
      <Fullscreen.Header>
        <Heading>
          <Fullscreen.Title>
            <FormattedMessage id="Listings.ListView.dealGraph.title" />
          </Fullscreen.Title>
          <Subtitle>
            <Description>
              <FormattedMessage
                id="Listings.ListView.dealGraph.subtitle"
                values={position}
              />
            </Description>
            <CompactDescription>
              <FormattedMessage
                id="Listings.ListView.dealGraph.subtitleCompact"
                values={position}
              />
            </CompactDescription>
          </Subtitle>
        </Heading>
        <HeaderCloseButton
          aria-label={formatMessage({
            id: 'Listings.ListView.dealGraph.closeIcon',
          })}
          onClick={onClose}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </HeaderCloseButton>
      </Fullscreen.Header>
      <Fullscreen.Body>
        <ModalSnackbarContainer containerId={SNACKBAR_CONTAINER_ID} />
        {!isFetching && data.length === 0 ? (
          /*
           * The one empty case the graph owns: a populated page in which no
           * listing has a typical selling price. A search with no results never
           * gets this far — ALP shows its own no-results state and the graph's
           * control is disabled.
           */
          <Centered>
            <EmptyState
              icon={faChartScatter}
              title={
                <FormattedMessage id="Listings.ListView.dealGraph.empty.title" />
              }
              description={
                <FormattedMessage id="Listings.ListView.dealGraph.empty.description" />
              }
            />
          </Centered>
        ) : (
          <>
            <DealGraphLegend />
            <DealGraph data={data} isFetching={isFetching} />
          </>
        )}
      </Fullscreen.Body>
      <Fullscreen.Footer>
        <FooterStart>
          {/*
           * Hidden while fetching, when it would describe the previous page, and
           * at zero, when no listing is missing from the graph to explain.
           */}
          {!isFetching && excludedCount > 0 && (
            <Excluded>
              <Description>
                <FormattedMessage
                  id="Listings.ListView.dealGraph.counts.excluded"
                  values={{
                    excluded: excludedCount,
                    total: data.length + excludedCount,
                  }}
                />
              </Description>
              <CompactDescription>
                <FormattedMessage
                  id="Listings.ListView.dealGraph.counts.excludedCompact"
                  values={{ excluded: excludedCount }}
                />
              </CompactDescription>
            </Excluded>
          )}
          {/* The list is the table this modal sits over. */}
          <ListButton
            appearance="tertiary"
            onClick={onClose}
            size={ButtonSizes.small}
            type="button"
          >
            <FormattedMessage id="Listings.ListView.dealGraph.viewAsList" />
          </ListButton>
        </FooterStart>
        <CloseButton appearance="primary" onClick={onClose} type="button">
          <FormattedMessage id="Listings.ListView.dealGraph.close" />
        </CloseButton>
      </Fullscreen.Footer>
    </InsetModal>
  )
}
